import { dayjs } from '@tunasong/models'
import type { GameResult, Persisted, UserStats } from '@tunasong/schemas'
import {Typography, UserAvatar, LinearProgress, ListItem, Tab, Tabs, List, ListItemText, ListItemAvatar, Table, TableBody, TableRow, TableCell} from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import type { Game } from '@tunasong/schemas'
import styles from './score-panel.module.css'
export interface ScorePanelProps {
  game: Game
  leaderBoard?: Persisted<GameResult>[]
  score: number
  userStats?: UserStats
  bonusTime?: number
  maxBonusTime?: number
}

export const ScorePanel: FC<ScorePanelProps> = props => {
  const { score = 0, userStats, bonusTime = 0, maxBonusTime = 10, leaderBoard } = props
  const { xp, scoreHigh } = userStats ?? { xp: 0, scoreSum: 0 }
  const [tab, setTab] = useState(0)

  return (
    <>
      <div className={styles.root}>
        <div className={styles.container}>
          <Tabs
            value={tab}
            onChange={(_ev, v) => setTab(v as number)}
            aria-label="score panel tabs"
            className={styles.tabs}
          >
            <Tab label="Score" />
            <Tab label="User Stats" />
            <Tab label="Leaderboard" />
          </Tabs>
          <div className={styles.content}>
            {tab === 0 && (
              <>
                <div className={styles.scoreDisplay}>
                  <Typography variant="h2">{score}</Typography>
                  <Typography variant="h5">Score</Typography>
                </div>
                {bonusTime > 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary" className={styles.bonusLabel}>
                      Bonus Time Remaining
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(bonusTime / maxBonusTime) * 100}
                      className={styles.progressBar}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      className={styles.bonusCaption}
                    >
                      {Math.ceil(bonusTime)}s
                    </Typography>
                  </>
                ) : null}
              </>
            )}
            {tab === 1 && (
              <div className={styles.tabContent}>
                {userStats ? (
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Streak</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{userStats.streak}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Level</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {userStats.level.name} ({userStats.level.description})
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Proficiency</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{userStats.level.proficiency}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Next level</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{userStats.level.maxScore + 1 - xp} XP remaining</Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">Your high score</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{scoreHigh}</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="subtitle2">XP</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{xp}</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No user stats available.
                  </Typography>
                )}
              </div>
            )}
            {tab === 2 && (
              <div className={styles.tabContent}>
                {leaderBoard && leaderBoard.length > 0 ? (
                  <List>
                    {leaderBoard.map((entry, idx) => (
                      <ListItem key={entry.userId} secondaryAction={entry.score}>
                        <ListItemAvatar>
                          <UserAvatar userId={entry.userId} size="small" />
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${idx + 1}. ${entry.nickName}`}
                          secondary={dayjs(entry.createdAt).format('MMM D, YYYY')}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No leaderboard data.
                  </Typography>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ScorePanel
